import type { BlindStructureDto } from '@pokerroom/types';

type DeadlineLevel = {
  levelNo: number;
  isBreak: boolean;
  smallBlind?: number;
  bigBlind?: number;
  durationSecs?: number | null;
};

export interface DeadlineOption {
  value: number;
  label: string;
}

export function clampDeadlineToOptions(
  value: number | undefined,
  optionCount: number,
): number | undefined {
  if (value === undefined || optionCount === 0) return value;
  return Math.min(value, optionCount);
}

export function effectiveLevelDuration(level: DeadlineLevel, levelSecs: number): number {
  return level.durationSecs ?? levelSecs;
}

export function buildDeadlineOptions(
  structure: Pick<BlindStructureDto, 'levelSecs' | 'levels'> | undefined,
): DeadlineOption[] {
  if (!structure) return [];
  let displayLevel = 0;
  return structure.levels.flatMap((level, rowIndex) => {
    if (level.isBreak) return [];
    displayLevel += 1;
    const minutes = effectiveLevelDuration(level, structure.levelSecs) / 60;
    const duration = Number.isInteger(minutes) ? String(minutes) : minutes.toFixed(1);
    return [
      {
        value: displayLevel,
        label: `Level ${displayLevel} — row #${rowIndex + 1} — ${(level.smallBlind ?? 0).toLocaleString('en-US')}/${(level.bigBlind ?? 0).toLocaleString('en-US')} — ${duration} min`,
      },
    ];
  });
}

export function calculateDeadlineCountdown({
  levels,
  levelSecs,
  currentLevelNo,
  currentRemainingSeconds,
  deadlineDisplayLevel,
}: {
  levels: DeadlineLevel[];
  levelSecs: number;
  currentLevelNo: number;
  currentRemainingSeconds: number;
  deadlineDisplayLevel: number | null | undefined;
}): { isOpen: boolean; remainingSeconds: number } {
  if (deadlineDisplayLevel == null || deadlineDisplayLevel <= 0) {
    return { isOpen: false, remainingSeconds: 0 };
  }

  let currentDisplayLevel = 0;
  for (const level of levels) {
    if (level.levelNo > currentLevelNo) break;
    if (!level.isBreak) currentDisplayLevel += 1;
  }
  if (currentDisplayLevel > deadlineDisplayLevel) {
    return { isOpen: false, remainingSeconds: 0 };
  }

  let remainingSeconds = currentRemainingSeconds;
  let displayLevel = currentDisplayLevel;
  for (const level of levels) {
    if (level.levelNo <= currentLevelNo) continue;
    if (!level.isBreak) displayLevel += 1;
    if (displayLevel > deadlineDisplayLevel) break;
    remainingSeconds += effectiveLevelDuration(level, levelSecs);
  }
  return { isOpen: true, remainingSeconds };
}
